import { TabContext, TabList } from "@mui/lab";
import {
  Box,
  Grid,
  IconButton,
  InputLabel,
  styled,
  Tab,
  TableCell,
  tableCellClasses,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import DataNotFound from "../Components/Common/DataNotFound";
import CustomPagination from "../Components/Common/CommonPagination";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  getAllClaims,
  getAllEligibleMembers,
  updateClaim,
} from "../Redux/Actions/action";
import ErrorHandler, { CommonStatusChip } from "../Utils/handler";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { BucketURL } from "../ApiSetup/Api";
import ButtonContained from "../Components/Common/ButtonContained";
import CommonTextField from "../Components/Common/CommonTextField";
import CustomDialog from "../Components/Common/CustomDialog";
import CommonFileUpload from "../Components/Common/CommonFileUpload";
import TextLabel from "../Components/Common/CommonTextLabel";
import Assets from "../Components/Common/Assets";
import EditIcon from "@mui/icons-material/Edit";
import CommonTable from "../Components/Common/CommonTable";
import { showLoader } from "../Redux/Slices/loaderSlice";
import dayjs from "dayjs";
import CommonRadioButton from "../Components/Common/CommonRadio";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme?.palette?.bgLightWhite?.main,
    fontSize: 14,
    // fontWeight: "bold",
    textAlign: "center", // Horizontally align content,
    lineHeight: "1.2rem",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "10px 10px !important",
    textAlign: "center", // Horizontally align content
    borderBottom: "2px solid #d9d6f3",
  },
}));

const Claim = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("0");
  const [data, setData] = useState({});
  const [claimsData, setClaimsData] = useState({});
  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [status, setStatus] = React.useState("Approved");
  const [rejectionReason, setRejectionReason] = React.useState("");

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const tabData = [
    { label: "Eligible Member", value: "0", color: theme.palette.primary.main },
    { label: "Claims Status", value: "1", color: theme.palette.primary.main },
  ];

  const _getApi = async () => {
    dispatch(showLoader(true));
    let body = {
      page: page,
      limit: rowsPerPage,
    };
    try {
      const result = await dispatch(
        value == "0" ? getAllEligibleMembers(body) : getAllClaims(body)
      );
      const error = ErrorHandler(result);
      if (error) {
        setClaimsData(result?.payload?.data);
      }
    } catch (error) {
      toast.error(error);
    } finally {
      dispatch(showLoader(false));
    }
  };

  const handleClear = () => {
    setOpen(false);
    setData({});
    setStatus("Approved");
    setRejectionReason("");
  };

  const _updateClaim = async () => {
    if (status === "Rejected" && !rejectionReason) {
      toast.error("Please enter Remark");
      return;
    }
    dispatch(showLoader(true));
    try {
      let body = {
        claimId: data?._id,
        status: status,
      };
      if (status === "Approved") {
        body.claimApprovedDate = dayjs();
      }
      if (status === "Rejected") {
        body.claimRejectedReason = rejectionReason;
        body.claimRejectedDate = dayjs();
      }
      const result = await dispatch(updateClaim(body));
      const error = ErrorHandler(result);
      if (error) {
        toast.success(result?.payload?.message);
        _getApi();
        handleClear();
      }
    } catch (error) {
      toast.error(error?.message || "An unknown error occurred");
    } finally {
      dispatch(showLoader(false));
    }
  };

  useEffect(() => {
    _getApi();
  }, [value, page, rowsPerPage]);

  const headerList =
    value == "0"
      ? [
          "No",
          "PPSK ID",
          "Member Name",
          "Mobile No",
          "Coverage Amount",
          "Start Date",
          "End Date",
          "Remaining Days",
          "Status",
        ]
      : [
          "No",
          "PPSK ID",
          "Member Name",
          "Claim Amount",
          "Death Date",
          "Claim Date",
          "Rejection Reason",
          "Status",
          "Action",
        ];

  return (
    <div>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "20px", fontWeight: 500, mb: 2 }}>
          Claim Records
        </Typography>
      </Box>
      <TabContext value={value}>
        <Box mb={1}>
          <TabList
            onChange={(e, newValue) => {
              if (newValue !== undefined) {
                setValue(newValue);
                setPage(1);
              }
            }}
            aria-label="tabs with icons"
            sx={{
              "& .MuiTabs-indicator": {
                display: "none",
              },
              "& .Mui-selected": {
                background: tabData?.filter((e) => e?.value == value)?.[0]
                  ?.color,
                color: `${theme.palette.common.white} !important`,
              },
            }}
          >
            {tabData?.map((tab) => {
              return (
                <Tab
                  label={`${tab?.label}`}
                  value={tab?.value}
                  sx={{
                    marginRight: "10px",
                    border: `2px solid ${tab.color}`,
                    borderRadius: "10px",
                    color: tab.color,
                    height: "40px",
                    minHeight: "0px",
                  }}
                />
              );
            })}
          </TabList>
        </Box>
        <Grid item xs={12}>
          <CommonTable headerList={headerList?.filter((e) => e)} isNotSorting>
            {claimsData?.[value == "0" ? "eligibleMembers" : "response"]
              ?.length > 0 ? (
              claimsData?.[value == "0" ? "eligibleMembers" : "response"]?.map(
                (item, index) =>
                  value == "0" ? (
                    <TableRow key={index}>
                      <StyledTableCell align="center">
                        {(index % rowsPerPage) + 1 + (page - 1) * rowsPerPage}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item?.ppskId}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item?.fullName}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item?.phoneNumber}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item?.claimsDetails?.[0]?.claimAmount
                          ? "₹" +
                            Number(
                              item?.claimsDetails?.[0]?.claimAmount
                            ).toLocaleString("en-IN")
                          : "-"}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {dayjs(item?.policyStatus?.activeAt).format(
                          "DD MMM YYYY"
                        )}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {dayjs(item?.policyStatus?.expireAt).format(
                          "DD MMM YYYY"
                        )}
                      </StyledTableCell>
                      <StyledTableCell
                        align="center"
                        sx={{
                          color:
                            item?.policyStatus?.remainingRenewDays > 30
                              ? "black"
                              : item?.policyStatus?.remainingRenewDays > 10
                              ? "rgba(245, 134, 52, 1)"
                              : "rgba(227, 54, 41, 1)",
                        }}
                      >
                        {item?.policyStatus?.remainingRenewDays
                          ? item?.policyStatus?.remainingRenewDays + " Days"
                          : "0 Days"}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {
                          <CommonStatusChip
                            label={
                              !item?.policyStatus?.remainingRenewDays
                                ? "Inactive"
                                : "Active"
                            }
                          />
                        }
                      </StyledTableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={index}>
                      <StyledTableCell align="center">
                        {(index % rowsPerPage) + 1 + (page - 1) * rowsPerPage}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item?.userDetails?.ppskId}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item?.userDetails?.fullName}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item?.claimAmount
                          ? "₹" +
                            Number(item.claimAmount).toLocaleString("en-IN")
                          : "-"}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {dayjs(item?.deathDate).format("DD MMM YYYY")}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {dayjs(item?.createdAt).format("DD MMM YYYY")}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item?.claimRejectedReason || "-"}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {<CommonStatusChip label={item?.status} />}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {item?.status === "Pending" && (
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            gap={1}
                            justifyContent={"center"}
                          >
                            <IconButton
                              sx={{ padding: 0 }}
                              onClick={() => {
                                setOpen(true);
                                setData(item);
                              }}
                            >
                              <EditIcon fontSize="small" color="success" />
                            </IconButton>
                          </Box>
                        )}
                      </StyledTableCell>
                    </TableRow>
                  )
              )
            ) : (
              <TableRow>
                <StyledTableCell colSpan={10}>
                  <DataNotFound
                    title={"No Records Found"}
                    icon={
                      <ErrorOutlineIcon
                        color="primary"
                        style={{ fontSize: "3rem" }}
                      />
                    }
                    elevation={0}
                  />
                </StyledTableCell>
              </TableRow>
            )}
          </CommonTable>
          {claimsData?.count > 0 && (
            <Grid item xs={12}>
              <CustomPagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                count={Math.ceil(claimsData?.count / rowsPerPage)}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Grid>
          )}
        </Grid>
      </TabContext>
      {open && (
        <CustomDialog
          isOpen={open}
          handleClose={handleClear}
          title={"PPSK Member"}
          padding={"10px"}
          maxWidth={"sm"}
          action={
            <>
              <ButtonContained
                text={"Update"}
                onClick={_updateClaim}
                minWidth={"100%"}
                marginTop={"10px"}
              />
            </>
          }
        >
          <Grid container spacing={2} xs={12}>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <InputLabel
                sx={{
                  letterSpacing: "0.03em",
                  marginRight: "2px",
                  fontSize: "14px",
                  color: "#222222",
                  whiteSpace: "wrap",
                  marginBottom: "0px",
                }}
              >
                Member Name
              </InputLabel>
              <Typography variant="body1" sx={{}}>
                {data?.userDetails?.fullName}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <InputLabel
                sx={{
                  letterSpacing: "0.03em",
                  marginRight: "2px",
                  fontSize: "14px",
                  color: "#222222",
                  whiteSpace: "wrap",
                  marginBottom: "0px",
                }}
              >
                Claims Amount
              </InputLabel>
              <Typography variant="body1" sx={{}}>
                {"₹" + Number(data?.claimAmount).toLocaleString("en-IN")}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12}>
              <CommonRadioButton
                text={"Status"}
                name={"status"}
                options={[
                  { value: "Approved", label: "Approve" },
                  { value: "Rejected", label: "Reject" },
                ]}
                value={status}
                onChange={(e) => setStatus(e?.target?.value)}
              />
            </Grid>
            {status === "Rejected" && (
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <CommonTextField
                  value={rejectionReason}
                  width={"100%"}
                  text="Remark"
                  type="text"
                  onChange={(e) => setRejectionReason(e?.target?.value)}
                  placeholder="Enter a Remark"
                  name="rejectionReason"
                  valid
                  multiline
                  rows={3}
                />
              </Grid>
            )}
          </Grid>
        </CustomDialog>
      )}
    </div>
  );
};

export default Claim;
